import { todayLocal } from '@/lib/dates'
import { useState } from 'react'
import {
  CalendarClock, Copy, Info, Link2, Plane, Plus, Trash2, UserCheck,
} from 'lucide-react'
import {
  ConfirmDialog, DateValue, EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import {
  useCalendarFeed, useCalendarFeedMutations, useDelegateCandidates, useDelegationMutations,
  useDelegations, useDelegationsToMe, useOutOfOffice,
} from '../api'
import type { CalendarSubscription, DelegationView } from '../types'



/**
 * Being away, and who signs while you are.
 *
 * <p>One screen for both because they are one fact. Somebody who delegates is out of office;
 * somebody who marks themselves away without delegating has left approvals waiting, which is a
 * legitimate choice but one the people waiting deserve to see. The calendar subscription is
 * here too, for the same reason a person visits this page at all: they are planning to be
 * somewhere else.
 */
export function DelegationPage() {
  useBreadcrumb()
  const delegations = useDelegations()
  const toMe = useDelegationsToMe()
  const away = useOutOfOffice()
  const feed = useCalendarFeed()
  const candidates = useDelegateCandidates()
  const { delegate, cancel, setAway, clearAway } = useDelegationMutations()
  const feedMutations = useCalendarFeedMutations()

  const [draft, setDraft] = useState<{
    toUserId: string; fromDate: string; toDate: string; entityKey: string; note: string
  } | null>(null)
  const [awayDraft, setAwayDraft] = useState<{
    fromDate: string; toDate: string; note: string
  } | null>(null)
  const [toCancel, setToCancel] = useState<DelegationView | null>(null)
  const [subscription, setSubscription] = useState<CalendarSubscription | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function run(work: () => Promise<unknown>, fallback: string) {
    setError(null)
    try {
      await work()
      return true
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? fallback)
      return false
    }
  }

  const subscriptionUrl = subscription
    ? new URL(subscription.url, window.location.origin).toString()
    : null

  return (
    <div className="space-y-4">
      <PageHeader
        title="Delegation and time away"
        subtitle="Hand your approvals to somebody while you are out, and say that you are out."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({ toUserId: '', fromDate: todayLocal(), toDate: todayLocal(), entityKey: '', note: '' })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            Delegate approvals
          </Button>
        }
      />

      {/* The same failure must not appear twice. While a dialog is open the reason belongs
          inside it, next to the field that caused it. */}
      {error && draft == null && awayDraft == null && (
        <Card role="alert" className="border-red-300 bg-red-50 p-3 text-sm text-red-900">
          {error}
        </Card>
      )}

      {/* ------------------------------------------------------------ away */}
      <Card className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 font-medium">
              <Plane className="size-4 text-slate-500" aria-hidden="true" />
              Out of office
            </h2>
            {away.isPending && <Skeleton className="mt-2 h-5 w-48" />}
            {away.isSuccess && (
              away.data.fromDate
                ? (
                  <p className="mt-1 text-sm text-slate-600">
                    <DateValue value={away.data.fromDate} />
                    {' – '}
                    <DateValue value={away.data.toDate ?? away.data.fromDate} />
                    {away.data.note ? ` · ${away.data.note}` : ''}
                    {away.data.awayToday && (
                      <span className="ms-2"><StatusBadge status="AWAY TODAY" /></span>
                    )}
                  </p>
                )
                : (
                  <p className="mt-1 text-sm text-slate-600">
                    You are not marked as away. Marking yourself away does not move your
                    approvals — delegate as well if somebody should sign in your place.
                  </p>
                )
            )}
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setError(null)
                setAwayDraft({
                  fromDate: away.data?.fromDate ?? todayLocal(),
                  toDate: away.data?.toDate ?? todayLocal(),
                  note: away.data?.note ?? '',
                })
              }}
            >
              <CalendarClock className="size-4" aria-hidden="true" />
              {away.data?.fromDate ? 'Change' : 'Mark me away'}
            </Button>
            {away.data?.fromDate && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => void run(() => clearAway.mutateAsync(), 'That could not be cleared')}
              >
                I am back
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* ------------------------------------------------------ delegations */}
      <Card>
        <h2 className="border-b border-slate-200 p-4 font-medium">Approvals I have delegated</h2>
        {delegations.isPending && <Skeleton className="m-4 h-20" />}
        {delegations.isError && (
          <ErrorState error={delegations.error} onRetry={() => void delegations.refetch()} />
        )}
        {delegations.isSuccess && delegations.data.length === 0 && (
          <EmptyState
            title="Nothing delegated"
            description="While you are away, approvals waiting on you stay where they are unless you hand them to somebody."
          />
        )}
        {delegations.isSuccess && delegations.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-start text-slate-600">
                <tr>
                  <th className="p-3 font-medium">To</th>
                  <th className="p-3 font-medium">Dates</th>
                  <th className="p-3 font-medium">Covers</th>
                  <th className="p-3 font-medium">Status</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {delegations.data.map((row) => (
                  <tr key={row.id} className="border-b border-slate-100">
                    <td className="p-3">{row.toUserName ?? row.toUserId}</td>
                    <td className="p-3 text-slate-600">
                      <DateValue value={row.fromDate} /> – <DateValue value={row.toDate} />
                    </td>
                    <td className="p-3 text-slate-600">
                      {row.entityKey ?? 'Every kind of approval'}
                    </td>
                    <td className="p-3">
                      <StatusBadge
                        status={!row.active ? 'CANCELLED' : row.liveToday ? 'ACTIVE' : 'SCHEDULED'}
                      />
                    </td>
                    <td className="p-3 text-end">
                      {row.active && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => { setError(null); setToCancel(row) }}
                        >
                          <Trash2 className="size-4" aria-hidden="true" />
                          Take back
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ------------------------------------------------------ handed to me */}
      {toMe.isSuccess && toMe.data.length > 0 && (
        <Card className="p-4">
          <h2 className="flex items-center gap-2 font-medium">
            <UserCheck className="size-4 text-slate-500" aria-hidden="true" />
            Approvals handed to you
          </h2>
          <ul className="mt-2 space-y-1 text-sm text-slate-600">
            {toMe.data.map((row) => (
              <li key={row.id}>
                {row.toUserName ?? 'Somebody'} — <DateValue value={row.fromDate} /> to{' '}
                <DateValue value={row.toDate} />
                {row.liveToday ? ' · live today' : ''}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* --------------------------------------------------------- calendar */}
      <Card className="p-4">
        <h2 className="flex items-center gap-2 font-medium">
          <Link2 className="size-4 text-slate-500" aria-hidden="true" />
          Calendar subscription
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          {feed.data?.note ?? 'Your approved leave, your team’s absences and the holidays, in a URL your calendar can subscribe to.'}
        </p>

        {subscriptionUrl && (
          <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <p className="break-all font-mono text-xs">{subscriptionUrl}</p>
            <p className="mt-2">{subscription?.note}</p>
            <div className="mt-2 flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void navigator.clipboard?.writeText(subscriptionUrl)}
              >
                <Copy className="size-4" aria-hidden="true" />
                Copy
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setSubscription(null)}
              >
                I have it
              </Button>
            </div>
          </div>
        )}

        <div className="mt-3 flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => void run(async () => {
              setSubscription(await feedMutations.issue.mutateAsync())
            }, 'A calendar URL could not be issued')}
          >
            {feed.data?.issued ? 'Generate a new URL' : 'Get my calendar URL'}
          </Button>
          {feed.data?.issued && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => void run(async () => {
                await feedMutations.revoke.mutateAsync()
                setSubscription(null)
              }, 'That subscription could not be stopped')}
            >
              Stop the subscription
            </Button>
          )}
        </div>

        <p className="mt-3 flex items-start gap-2 text-xs text-slate-500">
          <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          Anybody holding that URL can read the calendar it points at, so treat it like a
          password. Generating a new one stops the old one working immediately.
        </p>
      </Card>

      {/* ---------------------------------------------------------- dialogs */}
      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) setDraft(null) }}
        title="Delegate approvals"
        description="The person you choose can approve in your place between these dates."
        submitLabel="Delegate"
        loading={delegate.isPending}
        error={draft ? error : null}
        onSubmit={() => void (async () => {
          if (!draft) return
          const ok = await run(() => delegate.mutateAsync({
            toUserId: draft.toUserId,
            fromDate: draft.fromDate,
            toDate: draft.toDate,
            entityKey: draft.entityKey || null,
            note: draft.note || null,
          }), 'That delegation could not be created')
          if (ok) setDraft(null)
        })()}
      >
        <div className="space-y-3">
          <div>
            <Label htmlFor="delegate-to">To</Label>
            <Select
              id="delegate-to"
              value={draft?.toUserId ?? ''}
              onChange={(event) => setDraft((current) =>
                current ? { ...current, toUserId: event.target.value } : current)}
            >
              <option value="">Choose somebody</option>
              {candidates.data?.map((user) => (
                <option key={user.id} value={user.id}>{user.name || user.email}</option>
              ))}
            </Select>
            <p className="mt-1 text-xs text-slate-500">
              Somebody who is themselves delegating over these dates will be refused: approvals
              are resolved one hop, so a chain quietly stops moving.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="delegate-from">From</Label>
              <Input
                id="delegate-from"
                type="date"
                value={draft?.fromDate ?? ''}
                onChange={(event) => setDraft((current) =>
                  current ? { ...current, fromDate: event.target.value } : current)}
              />
            </div>
            <div>
              <Label htmlFor="delegate-to-date">To</Label>
              <Input
                id="delegate-to-date"
                type="date"
                value={draft?.toDate ?? ''}
                onChange={(event) => setDraft((current) =>
                  current ? { ...current, toDate: event.target.value } : current)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="delegate-note">Note</Label>
            <Input
              id="delegate-note"
              placeholder="At a conference"
              value={draft?.note ?? ''}
              onChange={(event) => setDraft((current) =>
                current ? { ...current, note: event.target.value } : current)}
            />
            <p className="mt-1 text-xs text-slate-500">
              Shown as your out-of-office note. Delegating marks you away over the same dates.
            </p>
          </div>
        </div>
      </FormDialog>

      <FormDialog
        open={awayDraft != null}
        onOpenChange={(open) => { if (!open) setAwayDraft(null) }}
        title="Mark yourself away"
        description="This says you are out. It does not move your approvals."
        submitLabel="Save"
        loading={setAway.isPending}
        error={awayDraft ? error : null}
        onSubmit={() => void (async () => {
          if (!awayDraft) return
          const ok = await run(() => setAway.mutateAsync({
            fromDate: awayDraft.fromDate,
            toDate: awayDraft.toDate,
            note: awayDraft.note || null,
          }), 'That could not be saved')
          if (ok) setAwayDraft(null)
        })()}
      >
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="away-from">From</Label>
              <Input
                id="away-from"
                type="date"
                value={awayDraft?.fromDate ?? ''}
                onChange={(event) => setAwayDraft((current) =>
                  current ? { ...current, fromDate: event.target.value } : current)}
              />
            </div>
            <div>
              <Label htmlFor="away-to">To</Label>
              <Input
                id="away-to"
                type="date"
                value={awayDraft?.toDate ?? ''}
                onChange={(event) => setAwayDraft((current) =>
                  current ? { ...current, toDate: event.target.value } : current)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="away-note">Note</Label>
            <Input
              id="away-note"
              placeholder="On annual leave"
              value={awayDraft?.note ?? ''}
              onChange={(event) => setAwayDraft((current) =>
                current ? { ...current, note: event.target.value } : current)}
            />
          </div>
        </div>
      </FormDialog>

      <ConfirmDialog
        open={toCancel != null}
        onOpenChange={(open) => { if (!open) setToCancel(null) }}
        title="Take this delegation back?"
        description={`Approvals will come back to you, and you will no longer be marked away unless something else says so.`}
        confirmLabel="Take back"
        onConfirm={() => void (async () => {
          if (!toCancel) return
          const ok = await run(() => cancel.mutateAsync(toCancel.id),
            'That delegation could not be cancelled')
          if (ok) setToCancel(null)
        })()}
      />
    </div>
  )
}
